import { defineDocument } from './types.js'
import {
  SYNCED_ACTIONS,
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, SENDABLE_ACTIONS,
  adjustmentsGrid, baseColumns, baseFilters, chargesGrid, col, f,
  currencySection, fcyColumns, interstate, jobType, pricedLines, quantityLines, taxUnit, termsSection,
} from './common.js'

/** docs/07 §1 — what the supplier billed us, against orders or progress. */
export const purchaseInvoice = defineDocument({
  key: 'purchase_invoice',
  group: 'Invoice',
  blurb: 'Create invoices for purchase and subcontract orders. You can also view existing invoices.',
  route: 'pur_invs',
  label: 'Purchase invoice',
  labelPlural: 'Purchase invoices',
  numbering: 'PURCHASE_INVOICE',
  numberPrefix: 'PI-',
  numberWidth: 8,
  workflow: 'PURCHASE_INVOICE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a purchase invoice',
    subtitle: 'Choose what the invoice is for, the supplier, and the orders it covers.',
    fields: [
      f.radio('invoiceFor', 'What is this invoice for?', [['PURCHASE_ORDERS', 'Purchase orders'], ['SUBCONTRACT', 'Labour or subcontract']]),
      f.look('supplierId', 'Supplier', 'supplier', true),
      { ...f.look('purchaseOrderId', 'Purchase order', 'purchase_order', true),
        visibleWhen: { field: 'invoiceFor', equals: ['PURCHASE_ORDERS'] } },
      { ...f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order'),
        visibleWhen: { field: 'invoiceFor', equals: ['SUBCONTRACT'] },
        hint: 'Leave it empty if no order was issued to the subcontractor.' },
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('invoiceFor', 'Invoice for', [['PURCHASE_ORDERS', 'Purchase orders'], ['SUBCONTRACT', 'Labour or subcontract']], false),
        f.look('supplierId', 'Supplier', 'supplier', true),
        f.look('purchaseOrderId', 'Purchase order', 'purchase_order'),
        f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order'),
        f.look('workProgressId', 'Work progress', 'work_progress'),
        // Naming the receipt narrows "Pull the open lines" to what actually
        // arrived on it, at the quantity that arrived — the vendor's rule is
        // "select the material from the received list", not from the ordered
        // one, and the two differ on every short delivery.
        { ...f.look('goodsReceiptId', 'Against receipt note', 'goods_receipt'),
          hint: 'Left empty the whole order is offered. Named, only what that receipt took in is.' },
        f.date('documentDate', 'Invoice date', true),
        f.text('supplierInvoiceNo', "Supplier's invoice no", 'half', true),
        f.date('supplierInvoiceDate', "Supplier's invoice date", true),
        f.look('projectId', 'Project', 'project'),
        jobType(),
        taxUnit(),
        interstate(),
        { key: 'creditPeriodDays', label: 'Credit period (days)', control: 'number', width: 'half',
          hint: 'The due date is worked out from the supplier invoice date and this.' },
        { key: 'dueDate', label: 'Payment due', control: 'date', width: 'half',
          hint: 'Left empty it is the supplier invoice date plus the credit period.' },
        f.notes(),
      ] },
      { key: 'billing', n: '02', title: 'Billing and delivery', fields: [
        f.look('billingAddressId', 'Bill to', 'address'),
        f.look('deliveryAddressId', 'Delivered to', 'address'),
        f.look('supplierContactId', "Supplier's contact", 'contact'),
      ] },
      currencySection(),
      termsSection(),
    ],
  },
  lines: pricedLines('Invoiced items', '03'),
  grids: [chargesGrid(), adjustmentsGrid()],
  columns: [...baseColumns('supplier', 'Supplier', 'Invoice value'), ...fcyColumns('Invoice value')],
  filters: [
    ...baseFilters('supplier', 'Supplier'),
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
  ],
  actions: SYNCED_ACTIONS,
  rules: ['rate_exceeds_estimation', 'progress_not_invoiceable', 'hsn_missing'],
  allocatesFrom: ['purchase_order', 'subcontract_order', 'goods_receipt', 'work_progress'],
} as const)

/**
 * docs/24 §3.8 — what goes back to a supplier, and what they owe for it.
 *
 * "Raise a debit note, or an urgent product replacement." Material rejected
 * at inspection never entered stock and never counted against the order; the
 * debit note records it leaving, and says whether the supplier is to refund
 * it or replace it. A replacement leaves the order open for the new delivery;
 * a debit is money the supplier owes, set off against their invoice.
 *
 * Material found damaged after it was accepted is different: it IS in stock,
 * so returning it takes it out of the lot it was received into.
 */
export const DEBIT_REASONS = [
  ['QC_REJECTION', 'Rejected at inspection'],
  ['RETURN_FROM_STOCK', 'Returned from stock — damaged or defective'],
  ['SHORT_SUPPLY', 'Billed but not delivered'],
  ['RATE_DIFFERENCE', 'Charged above the order rate'],
] as const satisfies ReadonlyArray<[string, string]>

export const debitNote = defineDocument({
  key: 'debit_note',
  group: 'Invoice',
  blurb: 'Return rejected or defective material to a supplier, for a refund or a replacement.',
  route: 'debit_notes',
  label: 'Debit note',
  labelPlural: 'Debit notes',
  numbering: 'DEBIT_NOTE',
  numberPrefix: 'DN-',
  workflow: 'DEBIT_NOTE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Raise a debit note',
    subtitle: 'Choose the receipt the material came in on, and why it is going back.',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.look('goodsReceiptId', 'Receipt note', 'goods_receipt', true, 'full'),
      f.radio('reason', 'Why is it going back?', DEBIT_REASONS),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.look('supplierId', 'Supplier', 'supplier', true), lockAfterDraft: true },
        { ...f.look('goodsReceiptId', 'Receipt note', 'goods_receipt', true), lockAfterDraft: true },
        f.look('purchaseInvoiceId', 'Against their invoice', 'purchase_invoice'),
        f.date('documentDate', 'Date', true),
        { ...f.choice('reason', 'Why is it going back?', DEBIT_REASONS), lockAfterDraft: true },
        f.choice('settlement', 'What the supplier is to do', [
          ['DEBIT', 'Refund — debit their account'],
          ['REPLACEMENT', 'Replace it — urgently'],
        ]),
        { ...f.date('replaceBy', 'Replacement needed by'),
          visibleWhen: { field: 'settlement', equals: ['REPLACEMENT'] } },
        f.text('returnedVia', 'Returned via (vehicle / LR)'),
        f.look('projectId', 'Project', 'project'),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials going back', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'rejectionReason', label: 'Why', type: 'text', width: 180, editable: true,
        options: [
          { value: 'DAMAGED', label: 'Damaged in transit' },
          { value: 'SPEC_MISMATCH', label: 'Not to specification' },
          { value: 'WRONG_ITEM', label: 'Wrong item or make' },
          { value: 'QUALITY_FAIL', label: 'Failed quality test' },
          { value: 'NO_CERTIFICATE', label: 'No test certificate' },
          { value: 'DEFECTIVE', label: 'Found defective in use' },
          { value: 'OTHER', label: 'Other' },
        ] },
      col.qty('Returned'), col.unit(false), col.rate(), col.hsn(), col.tax(), col.amount('Debit value'),
    ],
    sources: [{ key: 'rejection', label: 'Pick from the receipt', source: 'rejection' }],
  },
  columns: [
    ...baseColumns('supplier', 'Supplier', 'Debit value'),
    { key: 'settlement', label: 'Settlement', type: 'text', width: 140 },
  ],
  filters: baseFilters('supplier', 'Supplier'),
  actions: SENDABLE_ACTIONS,
  rules: ['debit_exceeds_rejection', 'insufficient_stock'],
} as const)

/** docs/07 §2 — asking the customer to pay for completed work. */
export const billingRequest = defineDocument({
  key: 'billing_request',
  group: 'Invoice',
  blurb: 'Request the customer to pay for completed work, with previous invoice details included.',
  route: 'bill_reqs',
  label: 'Billing request',
  labelPlural: 'Billing requests',
  numbering: 'BILLING_REQUEST',
  numberPrefix: 'BR No.{CO}',
  workflow: 'BILLING_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a billing request',
    subtitle: 'Select the project this covers.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      f.look('projectId', 'Project', 'project', true),
      f.radio('requestType', 'What kind of request?', [['INVOICE', 'Invoice'], ['CREDIT_NOTE', 'Credit note']]),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('customerId', 'Customer', 'customer', true),
        f.look('projectId', 'Project', 'project', true),
        f.date('documentDate', 'Request date', true),
        f.text('requestNo', 'Running bill no', 'half'),
        f.date('periodFrom', 'Period from'),
        f.date('periodTo', 'Period to'),
        f.choice('requestType', 'Kind', [['INVOICE', 'Invoice'], ['CREDIT_NOTE', 'Credit note']], false),
        jobType(),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Work billed', n: '02',
    columns: [
      col.seq(), col.item('BOQ line'), col.desc(), col.boqRef(),
      { key: 'billingTermLineId', label: 'Billing term', type: 'text', width: 190 },
      // What the BOQ ordered, read off the line this one was picked from.
      // Derived on every read, never stored: the request does not own it.
      { key: 'sourceQty', label: 'BOQ qty', type: 'qty', width: 108, align: 'right' },
      // What the work says may be billed, against what has been billed before.
      // The vendor puts all three on the screen — BOQ, Calculated, Billing —
      // because a billing quantity typed without them in view is a guess
      // (docs/video-notes/06 §7).
      { key: 'calculatedQty', label: 'Calculated', type: 'qty', width: 122, align: 'right' },
      { key: 'billedQty', label: 'Billed before', type: 'qty', width: 130, align: 'right' },
      /*
        The claim itself, and the column that is actually billed.

        It used to be a separate `requestQty` that rode in `attrs` and was read
        by nothing, while `quantity` — the column the amount, the tax and the
        ledger allocation all follow — was labelled 'BOQ qty' and locked. The
        quantity the operator typed was discarded on every request.
      */
      col.qty('Billing qty', true),
      col.unit(), col.rate(), col.hsn(), col.tax(), col.amount(),
    ],
    sources: [{ key: 'boq', label: 'Pick from the BOQ', source: 'boq' }],
  },
  grids: [chargesGrid(), adjustmentsGrid()],
  columns: [
    ...baseColumns('customer', 'Customer', 'Requested'),
    { key: 'fulfilmentStatus', label: 'Invoiced', type: 'text', width: 190 },
  ],
  filters: baseFilters('customer', 'Customer'),
  // The annexure is downloadable here, before any invoice exists — that copy
  // is what the customer acknowledges (docs/video-notes/06 §6).
  actions: [...FULL_ACTIONS, 'raBill'],
  rules: ['billing_request_exceeds_boq', 'hsn_missing'],
  createsNext: [
    { docType: 'sales_invoice', label: 'Sales invoice', whenStatus: 'APPROVED' },
    { docType: 'credit_note', label: 'Credit note', whenStatus: 'APPROVED' },
  ],
} as const)

/** docs/07 §3 — the statutory tax invoice raised to the customer. */
export const salesInvoice = defineDocument({
  key: 'sales_invoice',
  group: 'Invoice',
  blurb: 'Create invoices for billing requests. You can also view existing invoices.',
  route: 'sale_invs',
  label: 'Sales invoice',
  labelPlural: 'Sales invoices',
  numbering: 'SALES_INVOICE',
  numberPrefix: 'INV-',
  workflow: 'SALES_INVOICE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a sales invoice',
    subtitle: 'Select the project and the billing request it invoices.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      f.look('projectId', 'Project', 'project', true),
      f.look('billingRequestId', 'Billing request', 'billing_request', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('customerId', 'Customer', 'customer', true),
        f.look('projectId', 'Project', 'project', true),
        f.look('billingRequestId', 'Billing request', 'billing_request', true),
        f.date('documentDate', 'Invoice date', true),
        taxUnit(),
        jobType(),
        interstate(),
        { key: 'creditPeriodDays', label: 'Credit period (days)', control: 'number', width: 'half' },
        { key: 'dueDate', label: 'Payment due', control: 'date', width: 'half',
          hint: 'Left empty it is the invoice date plus the credit period.' },
        f.text('customerPoNo', 'Customer PO no'),
        f.date('customerPoDate', 'Customer PO date'),
        // Asked only where the company registers its invoices at all. Shown
        // unconditionally, they were two blanks on every invoice of a tenant
        // that had the switch off.
        { ...f.text('ackNo', 'e-Invoice ack no'), requiresSetting: 'einvoice_enabled' },
        { ...f.date('ackDate', 'e-Invoice ack date'), requiresSetting: 'einvoice_enabled' },
        { key: 'documentNo', label: 'Invoice number', control: 'text', width: 'half',
          requiresSetting: 'manual_numbering_invoices',
          hint: 'Left empty the next number in the series is used.' },
        f.notes(),
      ] },
      { key: 'billing', n: '02', title: 'Billing and delivery', fields: [
        f.look('billingAddressId', 'Bill to', 'address', true),
        f.look('deliveryAddressId', 'Ship to', 'address'),
        f.look('contactId', 'Billing contact', 'contact'),
      ] },
      currencySection(),
      termsSection(),
    ],
  },
  lines: pricedLines('Invoiced work', '03', 'BOQ line'),
  grids: [chargesGrid(), adjustmentsGrid()],
  columns: [...baseColumns('customer', 'Customer', 'Invoice value'), ...fcyColumns('Invoice value')],
  filters: baseFilters('customer', 'Customer'),
  // Same annexure again, now carrying the invoice number the customer's
  // accounts department will quote back.
  actions: [...SYNCED_ACTIONS, 'raBill'],
  rules: ['nothing_left_to_invoice', 'hsn_missing'],
  allocatesFrom: ['billing_request'],
  createsNext: [{ docType: 'credit_note', label: 'Credit note', whenStatus: 'APPROVED' }],
} as const)

/** docs/07 §4 — reversing part of a sales invoice. */
export const creditNote = defineDocument({
  key: 'credit_note',
  group: 'Invoice',
  blurb: 'Raise credit notes against billing requests. You can also view existing credit notes.',
  route: 'credit_notes',
  label: 'Credit note',
  labelPlural: 'Credit notes',
  numbering: 'CREDIT_NOTE',
  numberPrefix: 'CN-',
  numberWidth: 3,
  workflow: 'CREDIT_NOTE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a credit note',
    subtitle: 'Select the project and the billing request being credited.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      f.look('projectId', 'Project', 'project', true),
      f.look('billingRequestId', 'Billing request', 'billing_request', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('customerId', 'Customer', 'customer', true),
        f.look('projectId', 'Project', 'project', true),
        // The wizard asked for the billing request, so the form shows the same
        // one. An earlier version asked for an invoice here and the two
        // disagreed about what the note was against.
        f.look('billingRequestId', 'Billing request', 'billing_request', true),
        f.look('salesInvoiceId', 'Against invoice', 'sales_invoice'),
        f.date('documentDate', 'Credit note date', true),
        f.text('reason', 'Reason', 'full', true),
        taxUnit(),
        interstate(),
        { key: 'documentNo', label: 'Credit note number', control: 'text', width: 'half',
          requiresSetting: 'manual_numbering_invoices' },
        f.notes(),
      ] },
      { key: 'billing', n: '02', title: 'Billing', fields: [
        f.look('billingAddressId', 'Bill to', 'address'),
      ] },
    ],
  },
  lines: pricedLines('Credited items', '03', 'BOQ line'),
  grids: [adjustmentsGrid()],
  columns: baseColumns('customer', 'Customer', 'Credited'),
  filters: baseFilters('customer', 'Customer'),
  actions: SYNCED_ACTIONS,
  rules: ['hsn_missing'],
  allocatesFrom: ['billing_request'],
} as const)

/** docs/07 §5 — goods leaving for site, with or without an invoice. */
export const deliveryChallan = defineDocument({
  key: 'delivery_challan',
  group: 'Invoice',
  blurb: 'Create a delivery challan for materials delivered to site. You can also view existing challans.',
  route: 'del_clns',
  label: 'Delivery challan',
  labelPlural: 'Delivery challans',
  numbering: 'DELIVERY_CHALLAN',
  numberPrefix: 'DC-',
  workflow: 'DELIVERY_CHALLAN',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a delivery challan',
    subtitle: 'Select the customer and the project the materials are going to.',
    fields: [
      f.radio('challanType', 'What kind of movement?', [
        ['DELIVERY_TO_SITE', 'Delivery to site'],
        ['RETURN_FROM_SITE', 'Return from site'],
        ['SITE_TO_SITE', 'Site to site'],
      ]),
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.look('customerId', 'Customer', 'customer'),
        f.date('documentDate', 'Challan date', true),
        // Required, because `challan_mixes_billable` returns early on anything
        // that is not YES or NO: left blank, the control that keeps a signed
        // challan usable as billing evidence simply did not run.
        f.choice('itemType', 'Chargeable to the customer?', [['YES', 'Billable'], ['NO', 'Non billable']], true),
        // What the goods are moving against. Without one of these the challan
        // declared `allocatesFrom` but had nothing to resolve it to, so "Pull
        // the open lines" was a button that always answered "nothing".
        f.look('purchaseOrderId', 'Against purchase order', 'purchase_order'),
        f.look('materialTransferId', 'Against material transfer', 'material_transfer'),
        // One mixed receipt is meant to produce two challans — the billable
        // one the customer signs at the gate, and the internal one. Without
        // the receipt as a source there was nothing to raise them from
        // (docs/video-notes/06 §2).
        f.look('goodsReceiptId', 'Against receipt note', 'goods_receipt'),
        f.look('warehouseId', 'Dispatched from', 'warehouse'),
        f.text('vehicleNo', 'Vehicle no'),
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
        f.date('requestDate', 'Request date'),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
      { key: 'delivery', n: '02', title: 'Delivery', fields: [
        f.look('dispatchAddressId', 'Dispatch from', 'address'),
        f.look('deliveryAddressId', 'Deliver to', 'address', true),
        f.look('billingAddressId', 'Bill to', 'address'),
        f.look('contactId', 'Site contact', 'contact'),
        f.text('deliveryCoordinator', 'Delivery coordinator'),
        f.text('deliveryContactNo', 'Delivery contact no'),
        f.text('attentionOf', 'For the attention of', 'full'),
      ] },
    ],
  },
  lines: quantityLines('Materials delivered', '03', 'Delivered'),
  columns: [
    ...baseColumns('customer', 'Customer', 'Value'),
    { key: 'itemType', label: 'Item type', type: 'text', width: 150 },
  ],
  filters: baseFilters('customer', 'Customer'),
  // `splitBillable` is what makes the mixed case answerable: it moves the
  // lines that disagree with the header onto a challan of their own rather
  // than asking the operator to rebuild them by hand.
  actions: [...BASIC_ACTIONS, 'splitBillable'],
  rules: ['challan_exceeds_delivered', 'challan_mixes_billable', 'hsn_missing', 'insufficient_stock'],
  allocatesFrom: ['material_transfer', 'purchase_order', 'goods_receipt'],
} as const)
